// src/store/slices/cartSlice.js - WITH GST FIX FOR GUEST CART

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as cartApi from '../api/cart';

// Async thunks (unchanged)
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartApi.getCart();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addItem',
  async (itemData, { rejectWithValue }) => {
    try {
      const payload = {
        productId: itemData.productId,
        quantity: itemData.quantity || 1,
        color: itemData.color || null,
        dimensions: itemData.dimensions || null,
        customerNotes: itemData.customerNotes || '',
        urgency: itemData.urgency || 'WEEKLY',
        sizeLabel: itemData.sizeLabel || null,
        gstRate: itemData.gstRate ?? 18
      };
      
      const response = await cartApi.addToCart(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to add item to cart');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateItem',
  async (itemData, { rejectWithValue }) => {
    try {
      const response = await cartApi.updateCartItem(itemData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update cart item');
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeItem',
  async ({ productId, color, sizeLabel }, { rejectWithValue }) => {
    try {
      const response = await cartApi.removeFromCart(productId, color, sizeLabel);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to remove item from cart');
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartApi.clearCart();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to clear cart');
    }
  }
);

export const syncCart = createAsyncThunk(
  'cart/syncCart',
  async (guestCartData, { rejectWithValue }) => {
    try {
      const response = await cartApi.syncCart(guestCartData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to sync cart');
    }
  }
);

// Initial state
const initialState = {
  cart: {
    userId: null,
    items: [],
  },
  guestCart: {
    items: []
  },
  itemCount: 0,
  totalAmount: 0,
  subtotal: 0,
  discount: 0,
  gstAmount: 0,
  cgst: 0,
  sgst: 0,
  igst: 0,
  shippingCost: 0,
  isAuthenticated: false,
  loading: false,
  error: null,
};

// ✅ NEW: Helper to calculate GST (matching backend logic)
const calculateGST = (subtotal) => {
  const gstRate = 0.18;
  const gstAmount = Math.round(subtotal * gstRate);
  return {
    cgst: Math.round(gstAmount / 2),
    sgst: Math.round(gstAmount / 2),
    igst: 0,
    totalGst: gstAmount
  };
};

// ✅ UPDATED: Calculate cart totals WITH GST
const calculateCartTotals = (state) => {
  const cart = state.isAuthenticated ? state.cart : state.guestCart;
  
  // Calculate basic totals
  state.itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);
  state.subtotal = cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  
  // Calculate discount (MRP - Selling Price)
  const totalMrp = cart.items.reduce((total, item) => total + (item.mrp * item.quantity), 0);
  state.discount = totalMrp - state.subtotal;
  
  // ✅ Calculate GST for guest cart
  if (!state.isAuthenticated) {
    const gst = calculateGST(state.subtotal);
    state.gstAmount = gst.totalGst;
    state.cgst = gst.cgst;
    state.sgst = gst.sgst;
    state.igst = gst.igst;
    
    // Calculate shipping (basic logic - can be enhanced)
    const freeShippingMinimum = 5000;
    if (state.subtotal >= freeShippingMinimum) {
      state.shippingCost = 0;
    } else {
      state.shippingCost = 100; // Default shipping cost
    }
  } else {
    // For authenticated users, get GST from backend response
    if (state.cart.gstCalculation) {
      state.gstAmount = state.cart.gstCalculation.totalGst;
      state.cgst = state.cart.gstCalculation.cgst;
      state.sgst = state.cart.gstCalculation.sgst;
      state.igst = state.cart.gstCalculation.igst;
    }
    
    // Get shipping from backend
    state.shippingCost = state.cart.shipping?.cost || 0;
  }
  
  // Calculate final total
  state.totalAmount = state.subtotal + state.shippingCost + state.gstAmount;
  
  // console.log('💰 Cart Totals Calculated:', {
  //   subtotal: state.subtotal,
  //   discount: state.discount,
  //   gstAmount: state.gstAmount,
  //   cgst: state.cgst,
  //   sgst: state.sgst,
  //   shippingCost: state.shippingCost,
  //   totalAmount: state.totalAmount,
  //   isAuthenticated: state.isAuthenticated
  // });
};

// Helper to ensure stock data is valid
const normalizeStockData = (item) => {
  let stock = 0;
  
  // Check for numeric stock fields first
  if (item.stock || item.stockAvailable || item.quantity) {
    stock = item.stock || item.stockAvailable || item.quantity || 0;
  } 
  // Check for stockLevel string (your API uses this)
  else if (item.stockLevel) {
    const stockLevelMap = {
      'high': 100,
      'medium': 50,
      'low': 10,
      'out_of_stock': 0,
      'out-of-stock': 0
    };
    stock = stockLevelMap[item.stockLevel.toLowerCase()] || 0;
  }
  // Fallback to isInStock boolean
  else if (item.isInStock) {
    stock = 999;
  }
  
  return {
    stock: stock,
    stockAvailable: stock,
    isInStock: stock > 0,
    stockStatus: item.stockStatus || (stock === 0 ? 'OUT_OF_STOCK' : 
                 stock <= 10 ? 'LOW_STOCK' : 'IN_STOCK'),
    stockLevel: item.stockLevel // Preserve original stockLevel
  };
};

// Slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // Add to guest cart
    addToGuestCart: (state, action) => {
      const itemData = action.payload;
      
      // console.log('💾 Redux: Adding to guest cart', itemData);
      
      // Normalize stock data
      const stockData = normalizeStockData(itemData);
      
      // console.log('📊 Normalized stock data:', stockData);
      
      // Check if item exists
      const existingItemIndex = state.guestCart.items.findIndex(
        item => item.productId === itemData.productId &&
        item.color === itemData.color &&
        (item.sizeLabel || null) === (itemData.sizeLabel || null)
      );
      
      if (existingItemIndex !== -1) {
        // Update quantity
        state.guestCart.items[existingItemIndex].quantity += itemData.quantity;
        // Update stock data too
        Object.assign(state.guestCart.items[existingItemIndex], stockData);
        // console.log('💾 Updated existing item with fresh stock');
      } else {
        // Add new item with normalized stock data
        state.guestCart.items.push({
          // Basic info
          productId: itemData.productId,
          name: itemData.name,
          price: itemData.price,
          mrp: itemData.mrp,
          quantity: itemData.quantity,
          color: itemData.color,
          sizeLabel: itemData.sizeLabel || null,
          gstRate: itemData.gstRate ?? 18,

          
          // Images
          imageUrl: itemData.imageUrl,
          thumbnailUrl: itemData.thumbnailUrl,
          
          // CRITICAL: Use normalized stock data
          ...stockData,
          
          // Product details
          category: itemData.category,
          subCategory: itemData.subCategory,
          material: itemData.material,
          brand: itemData.brand,
          manufacturer: itemData.manufacturer,
          
          // Physical specs
          thickness: itemData.thickness,
          weight: itemData.weight,
          gsm: itemData.gsm,
          dimensions: itemData.dimensions,
          coverageArea: itemData.coverageArea,
          piecesPerPack: itemData.piecesPerPack || 1,
          rollLength: itemData.rollLength,
          rollWeight: itemData.rollWeight,
          
          // Order constraints
          minOrderQuantity: itemData.minOrderQuantity || 1,
          maxOrderQuantity: itemData.maxOrderQuantity,
          
          // Flags
          isEcoFriendly: itemData.isEcoFriendly || false,
          isBulkItem: itemData.isBulkItem || false,
          requiresSpecialHandling: itemData.requiresSpecialHandling || false,
          
          // Delivery
          estimatedDeliveryDays: itemData.estimatedDeliveryDays || 3,
          deliveryMessage: itemData.deliveryMessage || 'Delivery in 2-3 business days',
          
          // Optional
          customerNotes: itemData.customerNotes || '',
          urgency: itemData.urgency || 'WEEKLY'
        });
        
        // console.log('💾 Added new item with stock:', stockData);
      }
      
      // Save to localStorage
      localStorage.setItem('guestCart', JSON.stringify(state.guestCart));
      // console.log('💾 Guest cart saved to localStorage');
      
      // ✅ Update totals (now includes GST)
      calculateCartTotals(state);
    },
    
    updateGuestCartItem: (state, action) => {
      const { productId, quantity, color, sizeLabel } = action.payload;
      
      const itemIndex = state.guestCart.items.findIndex(
        item => item.productId === productId && item.color === color &&
        (item.sizeLabel || null) === (sizeLabel || null)
      );
      
      if (itemIndex !== -1) {
        if (quantity <= 0) {
          state.guestCart.items.splice(itemIndex, 1);
        } else {
          state.guestCart.items[itemIndex].quantity = quantity;
        }
        
        localStorage.setItem('guestCart', JSON.stringify(state.guestCart));
        calculateCartTotals(state);
      }
    },
    
    removeFromGuestCart: (state, action) => {
      const { productId, color } = action.payload;
      
      const itemIndex = state.guestCart.items.findIndex(
        item => item.productId === productId && (item.color === color || (color === null && item.color === null))
      );
      
      if (itemIndex !== -1) {
        state.guestCart.items.splice(itemIndex, 1);
        localStorage.setItem('guestCart', JSON.stringify(state.guestCart));
        calculateCartTotals(state);
      }
    },
    
    clearGuestCart: (state) => {
      state.guestCart.items = [];
      localStorage.removeItem('guestCart');
      calculateCartTotals(state);
    },
    
    loadGuestCartFromStorage: (state) => {
      const storedCart = localStorage.getItem('guestCart');
      
      // console.log('📥 Loading guest cart from localStorage');
      
      if (storedCart) {
        try {
          const parsedCart = JSON.parse(storedCart);
          
          // Normalize stock data for all items
          if (parsedCart.items && Array.isArray(parsedCart.items)) {
            parsedCart.items = parsedCart.items.map(item => ({
              ...item,
              ...normalizeStockData(item)
            }));
          }
          
          state.guestCart = parsedCart;
          // console.log(`✅ Loaded ${state.guestCart.items?.length || 0} items from localStorage`);
        } catch (error) {
          // console.error('❌ Error parsing guest cart:', error);
          state.guestCart = { items: [] };
        }
        
        calculateCartTotals(state);
      } else {
        // console.log('📭 No guest cart in localStorage');
      }
    },
    
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
      // console.log(`🔐 Auth state changed: ${action.payload}`);
      calculateCartTotals(state);
    },
    
    resetAuthenticatedCart: (state) => {
      state.cart = {
        userId: null,
        items: []
      };
      
      const storedGuestCart = localStorage.getItem('guestCart');
      if (storedGuestCart) {
        try {
          state.guestCart = JSON.parse(storedGuestCart);
        } catch (e) {
          state.guestCart = { items: [] };
        }
      }
      
      state.isAuthenticated = false;
      calculateCartTotals(state);
    }
  },
  extraReducers: (builder) => {
    // Fetch cart
    builder
      .addCase(fetchCart.pending, (state) => {
        // console.log('📦 Fetching cart...');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.cart = action.payload.data.cart;
        state.loading = false;
        calculateCartTotals(state);
      })
      .addCase(fetchCart.rejected, (state, action) => {
        // console.log('❌ Failed to fetch cart:', action.payload?.message);
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch cart';
      });
      
    // Add to cart
    builder
      .addCase(addToCart.pending, (state) => {
        // console.log('📦 Adding item to cart...');
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.cart = action.payload.data.cart;
        state.loading = false;
        calculateCartTotals(state);
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to add item';
      });
      
    // Update cart item
    builder
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.cart = action.payload.data.cart;
        state.loading = false;
        calculateCartTotals(state);
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update';
      });
      
    // Remove from cart
    builder
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.cart = action.payload.data.cart;
        state.loading = false;
        calculateCartTotals(state);
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to remove';
      });
      
    // Clear cart
    builder
      .addCase(clearCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state, action) => {
        state.cart = action.payload.data.cart;
        state.loading = false;
        calculateCartTotals(state);
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to clear';
      });
      
    // Sync cart
    builder
      .addCase(syncCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncCart.fulfilled, (state, action) => {
        state.cart = action.payload.data.cart;
        state.loading = false;
        state.guestCart = { items: [] };
        localStorage.removeItem('guestCart');
        calculateCartTotals(state);
      })
      .addCase(syncCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to sync';
      });
      
    // Handle logout
    builder.addMatcher(
      (action) => action.type === 'auth/logout' || action.type === 'auth/logoutUser/fulfilled',
      (state) => {
        state.cart = { userId: null, items: [] };
        state.isAuthenticated = false;
        calculateCartTotals(state);
      }
    );
  }
});

export const { 
  addToGuestCart, 
  updateGuestCartItem, 
  removeFromGuestCart,
  clearGuestCart,
  loadGuestCartFromStorage,
  setIsAuthenticated,
  resetAuthenticatedCart
} = cartSlice.actions;

// ✅ NEW: Selectors for accessing cart data with GST
export const selectCartSummary = (state) => ({
  itemCount: state.cart.itemCount,
  subtotal: state.cart.subtotal,
  discount: state.cart.discount,
  gstAmount: state.cart.gstAmount,
  cgst: state.cart.cgst,
  sgst: state.cart.sgst,
  igst: state.cart.igst,
  shippingCost: state.cart.shippingCost,
  totalAmount: state.cart.totalAmount
});

export const selectGstBreakdown = (state) => ({
  cgst: state.cart.cgst,
  sgst: state.cart.sgst,
  igst: state.cart.igst,
  totalGst: state.cart.gstAmount
});

export default cartSlice.reducer;